import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.06 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

// TikTok video IDs from @dralonsoashaika
const tiktokVideos = [
  {
    id: "profile",
    embedUrl: "https://www.tiktok.com/@dralonsoashaika",
    title: "Ver perfil en TikTok",
    isProfileLink: true,
  },
];

function TikTokCard({ video, index, inView }: { video: typeof tiktokVideos[0]; index: number; inView: boolean }) {
  const [loaded, setLoaded] = useState(false);

  if (video.isProfileLink) {
    return (
      <div
        className={`flex flex-col w-full max-w-xs transition-all duration-600 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        style={{ transitionDelay: `${index * 150}ms` }}
      >
        <div className="relative bg-[#0A1F44]/5 rounded-2xl overflow-hidden" style={{ paddingBottom: "177.77%", height: 0 }}>
          <a
            href={video.embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-[#0A1F44] to-[#0A1F44]/80 hover:from-[#0A1F44]/90 hover:to-[#0A1F44]/70 transition-all duration-300 cursor-pointer"
          >
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 mb-5 border-2 border-[#D4AF37]/50">
              <i className="ri-tiktok-line text-white text-4xl"></i>
            </div>
            <span className="text-[#D4AF37] font-bold text-lg mb-1">@dralonsoashaika</span>
            <span className="text-white/70 text-sm mb-6">Ver perfil completo</span>
            <span className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1F44] font-semibold text-sm px-6 py-2.5 rounded-full whitespace-nowrap">
              <i className="ri-external-link-line"></i>
              Ir a TikTok
            </span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col transition-all duration-600 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* TikTok embed iframe */}
      <div className="relative bg-[#0A1F44]/5 rounded-2xl overflow-hidden" style={{ paddingBottom: "177.77%", height: 0 }}>
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A1F44]/5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A1F44] mb-3">
              <i className="ri-tiktok-line text-white text-2xl"></i>
            </div>
            <span className="text-xs text-gray-400">Cargando video...</span>
          </div>
        )}
        <iframe
          src={video.embedUrl}
          className="absolute top-0 left-0 w-full h-full rounded-2xl"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          title={video.title}
          onLoad={() => setLoaded(true)}
          style={{ border: "none" }}
        ></iframe>
      </div>
    </div>
  );
}

export default function TikTokVideos() {
  const { ref, inView } = useInView();

  return (
    <section id="tiktok" className="py-20 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-14 md:mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A1F44]">
              <i className="ri-tiktok-line text-white text-base"></i>
            </div>
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">@dralonsoashaika</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Videos en TikTok
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Contenido educativo sobre medicina alternativa, biomagnetismo y bienestar integral. Síguenos para aprender cada día.
          </p>
        </div>

        {/* Videos grid */}
        <div className="flex justify-center">
          {tiktokVideos.map((video, i) => (
            <TikTokCard key={video.id} video={video} index={i} inView={inView} />
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-14 transition-all duration-700 delay-500 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            <a
              href="https://www.tiktok.com/@dralonsoashaika"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-[#0A1F44] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-tiktok-line text-base"></i>
              </div>
              Seguir en TikTok
            </a>
            <span className="text-gray-400 text-sm hidden sm:block">·</span>
            <p className="text-gray-500 text-sm">
              Más de <strong className="text-[#0A1F44]">350,000 seguidores</strong> aprendiendo sobre salud alternativa
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
